import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  ArrowRight, 
  CheckCircle, 
  Zap, 
  Target, 
  Users, 
  ChevronDown,
  ChevronUp,
  ExternalLink
} from 'lucide-react';

const ServiceSection = ({ service, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <section className={`py-24 ${index % 2 === 0 ? 'bg-background' : 'bg-muted/30'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Content Side */}
          <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
            {/* Service Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Zap className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">{service.title}</span>
            </div>

            {/* Title */}
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
              {service.tagline}
            </h2>

            {/* Hero Summary */}
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              {service.heroSummary}
            </p>

            {/* Benefits */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
                <Target className="h-6 w-6 text-primary" />
                Key Benefits
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {service.benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground leading-relaxed">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button size="lg" className="group">
                Get Started
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button variant="outline" size="lg" className="group">
                Learn More
                <ExternalLink className="ml-2 h-5 w-5 group-hover:scale-110 transition-transform" />
              </Button>
            </div>

            {/* Pricing Note */}
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/10">
              <p className="text-sm text-primary font-medium">
                💡 {service.pricing}
              </p>
            </div>
          </div>

          {/* Details Side */}
          <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
            <Card className="group hover:shadow-2xl transition-all duration-500 border-border/50 hover:border-primary/30 overflow-hidden">
              <CardContent className="p-0">
                {/* Header */}
                <div className="p-8 bg-gradient-to-r from-primary/5 to-primary/10 border-b border-border/50">
                  <h3 className="text-2xl font-bold text-foreground mb-2">{service.title}</h3>
                  <p className="text-muted-foreground">Comprehensive solution for your business needs</p>
                </div>

                {/* Expandable Content */}
                <div className="p-8">
                  {/* Deliverables */}
                  <div className="mb-8">
                    <h4 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      What You Get
                    </h4>
                    <div className="space-y-3">
                      {service.deliverables.slice(0, isExpanded ? service.deliverables.length : 3).map((deliverable, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></div>
                          <span className="text-muted-foreground text-sm">{deliverable}</span>
                        </div>
                      ))}
                      {service.deliverables.length > 3 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={toggleExpanded}
                          className="text-primary hover:text-primary/80 p-0 h-auto"
                        >
                          {isExpanded ? (
                            <>
                              Show Less <ChevronUp className="ml-1 h-4 w-4" />
                            </>
                          ) : (
                            <>
                              Show More <ChevronDown className="ml-1 h-4 w-4" />
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Integrations */}
                  <div className="mb-8">
                    <h4 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                      <Zap className="h-5 w-5 text-primary" />
                      Integrations
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {service.integrations.map((integration, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {integration}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Industries */}
                  <div>
                    <h4 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                      <Users className="h-5 w-5 text-primary" />
                      Perfect For
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {service.industries.map((industry, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {industry}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceSection;
